import React, { useContext, useState, useEffect, useMemo } from 'react';
import { Modal, Select, Slider } from 'antd';
import { useTranslation } from 'react-i18next';
import { ThemeContext } from '@/theme/ThemeProvider';
import BigNumber from 'bignumber.js';
import { getBigNumber } from '@utils/getBigNumber';
import * as S from './styles';
import { COLORS } from '@/app/constants';
import { ArrowDownOutlined } from '@ant-design/icons';

interface TradeCalculatorProps {
  open: boolean;
  onClose: () => void;
  EPs: any[];
  calcEP: number;
  setCalcEP: (id: number) => void;
}

const TradeCalculator: React.FC<TradeCalculatorProps> = ({
  open,
  onClose,
  EPs,
  calcEP,
  setCalcEP,
}) => {
  const { t } = useTranslation();
  const { theme, isDarkMode } = useContext(ThemeContext);

  // 상태
  const [tab, setTab] = useState<'PNL' | 'LIQUID'>('PNL');
  const [direction, setDirection] = useState<'LONG' | 'SHORT'>('LONG');
  const [leverage, setLeverage] = useState(100);

  // PNL 계산 입력
  const [entryPrice, setEntryPrice] = useState('');
  const [exitPrice, setExitPrice] = useState('');
  const [quantity, setQuantity] = useState('');

  // Liquidation 계산 입력
  const [margin, setMargin] = useState('');

  // 현재 선택된 거래쌍
  const currentEP = EPs?.find((item) => item.id === calcEP);
  const coin = currentEP?.pairs?.split('/')[0] || 'BTC';
  const maxLeverage = currentEP?.max_leverage || 500;

  // 레버리지 마커
  const leverageMarks: { [key: number]: string } = {};
  [1, 25, 50, 75, 100, maxLeverage].forEach((v) => {
    if (v <= maxLeverage) leverageMarks[v] = `${v}x`;
  });

  // PNL 계산
  const calculatePNL = () => {
    if (!entryPrice || !exitPrice || !quantity) {
      return { entryPriceResult: null, pnl: null, pnlPercent: null, roi: null };
    }

    const entry = getBigNumber(entryPrice);
    const exit = getBigNumber(exitPrice);
    const qty = getBigNumber(quantity);
    const lev = getBigNumber(leverage);

    let pnl: BigNumber;
    if (direction === 'LONG') {
      pnl = exit.minus(entry).multipliedBy(qty);
    } else {
      pnl = entry.minus(exit).multipliedBy(qty);
    }

    const margin = entry.multipliedBy(qty).dividedBy(lev);
    const pnlPercent = margin.isZero()
      ? getBigNumber(0)
      : pnl.dividedBy(margin).multipliedBy(100);
    const roi = pnlPercent;

    return {
      entryPriceResult: entry.multipliedBy(qty),
      pnl,
      pnlPercent,
      roi,
    };
  };

  // 청산가 계산
  const calculateLiquidation = () => {
    if (!entryPrice || !margin || !quantity) return null;

    const entry = getBigNumber(entryPrice);
    const mrg = getBigNumber(margin);
    const qty = getBigNumber(quantity);
    const lev = getBigNumber(leverage);

    const maintenanceRate = getBigNumber(0.004); // 0.4%

    let liquidPrice: BigNumber;
    if (direction === 'LONG') {
      liquidPrice = entry.minus(
        mrg.multipliedBy(getBigNumber(1).minus(maintenanceRate)).dividedBy(qty)
      );
    } else {
      liquidPrice = entry.plus(
        mrg.multipliedBy(getBigNumber(1).minus(maintenanceRate)).dividedBy(qty)
      );
    }

    return liquidPrice;
  };

  const pnlResult = tab === 'PNL' ? calculatePNL() : null;
  const liquidResult = tab === 'LIQUID' ? calculateLiquidation() : null;

  const SelectOptions = useMemo(() => {
    if (!EPs) return [];
    return EPs?.map((item) => {
      return {
        label: item.pairs?.replace('/', ''),
        value: item.id,
      };
    });
  }, [EPs]);

  const reset = () => {
    setEntryPrice('');
    setExitPrice('');
    setQuantity('');
    setMargin('');
    setLeverage(1);
    setDirection('LONG');
    setTab('PNL');
  };

  return (
    <S.CustomModal
      open={open}
      onCancel={onClose}
      footer={null}
      title={null}
      width={560}
      centered
    >
      <S.SelectDropdownGlobalStyle />
      <S.CalculatorContainer>
        {/* 상단 고정 영역 */}
        <S.TopSection>
          {/* 거래쌍 선택 드롭다운 */}
          <S.CoinSelect
            popupClassName="custom-select-dropdown"
            value={calcEP}
            onChange={(e: any) => {
              setCalcEP(Number(e));
              reset();
            }}
            options={SelectOptions}
            suffixIcon={<ArroawIcon />}
          />

          {/* 탭 (PNL Price / Liquidation Price) */}
          <S.TabSection>
            <S.TabButton $active={tab === 'PNL'} onClick={() => setTab('PNL')}>
              {t('pnlprice')}
            </S.TabButton>
            <S.TabButton
              $active={tab === 'LIQUID'}
              onClick={() => setTab('LIQUID')}
            >
              {t('LiquidationPrice')}
            </S.TabButton>
          </S.TabSection>
        </S.TopSection>

        {/* 하단 컨텐츠 영역 */}
        <S.ContentSection>
          <S.LeftSection>
            {/* Long/Short 버튼 */}
            <S.DirectionSection>
              <S.DirectionButton
                $active={direction === 'LONG'}
                $type="long"
                onClick={() => setDirection('LONG')}
              >
                {t('long')}
              </S.DirectionButton>
              <S.DirectionButton
                $active={direction === 'SHORT'}
                $type="short"
                onClick={() => setDirection('SHORT')}
              >
                {t('short')}
              </S.DirectionButton>
            </S.DirectionSection>

            {/* 레버리지 섹션 */}
            <S.LeverageSection>
              <S.LeverageLabel>{t('leverage')}</S.LeverageLabel>
              <S.LeverageValueBox>
                <span>{leverage}x</span>
              </S.LeverageValueBox>
              <S.SliderSection>
                <Slider
                  styles={{
                    rail: {
                      background: '#2A2C2E',
                      height: 3,
                    },
                    track: {
                      background: '#07D993',
                      height: 3,
                    },
                    handle: {
                      border: `1px solid #07D993`,
                      color: 'black',
                      background: '#07D993',
                    },
                  }}
                  min={1}
                  max={maxLeverage}
                  value={leverage}
                  onChange={setLeverage}
                  marks={leverageMarks}
                  tooltip={{ formatter: (value) => `${value}x` }}
                />
              </S.SliderSection>
            </S.LeverageSection>

            {/* PNL 계산 입력 */}
            {tab === 'PNL' && (
              <S.InputSection>
                <S.InputGroup>
                  <S.InputLabel>{t('entryPrice')}</S.InputLabel>
                  <S.InputWrapper>
                    <input
                      type="number"
                      placeholder="0"
                      value={entryPrice}
                      onChange={(e) => setEntryPrice(e.target.value)}
                    />
                    <S.InputSuffix>USDT</S.InputSuffix>
                  </S.InputWrapper>
                </S.InputGroup>

                <S.InputGroup>
                  <S.InputLabel>{t('exitPrice')}</S.InputLabel>
                  <S.InputWrapper>
                    <input
                      type="number"
                      placeholder="0"
                      value={exitPrice}
                      onChange={(e) => setExitPrice(e.target.value)}
                    />
                    <S.InputSuffix>USDT</S.InputSuffix>
                  </S.InputWrapper>
                </S.InputGroup>

                <S.InputGroup>
                  <S.InputLabel>{t('quantity')}</S.InputLabel>
                  <S.InputWrapper>
                    <input
                      type="number"
                      placeholder="0"
                      value={quantity}
                      onChange={(e) => setQuantity(e.target.value)}
                    />
                    <S.InputSuffix>USDT</S.InputSuffix>
                  </S.InputWrapper>
                </S.InputGroup>
              </S.InputSection>
            )}

            {/* 청산가 계산 입력 */}
            {tab === 'LIQUID' && (
              <S.InputSection>
                <S.InputGroup>
                  <S.InputLabel>{t('entryPrice')}</S.InputLabel>
                  <S.InputWrapper>
                    <input
                      type="number"
                      placeholder="0"
                      value={entryPrice}
                      onChange={(e) => setEntryPrice(e.target.value)}
                    />
                    <S.InputSuffix>USDT</S.InputSuffix>
                  </S.InputWrapper>
                </S.InputGroup>

                <S.InputGroup>
                  <S.InputLabel>{t('margin')}</S.InputLabel>
                  <S.InputWrapper>
                    <input
                      type="number"
                      placeholder="0"
                      value={margin}
                      onChange={(e) => setMargin(e.target.value)}
                    />
                    <S.InputSuffix>USDT</S.InputSuffix>
                  </S.InputWrapper>
                </S.InputGroup>

                <S.InputGroup>
                  <S.InputLabel>{t('quantity')}</S.InputLabel>
                  <S.InputWrapper>
                    <input
                      type="number"
                      placeholder="0"
                      value={quantity}
                      onChange={(e) => setQuantity(e.target.value)}
                    />
                    <S.InputSuffix>{coin}</S.InputSuffix>
                  </S.InputWrapper>
                </S.InputGroup>
              </S.InputSection>
            )}
          </S.LeftSection>

          {/* 오른쪽 결과 카드 */}
          <S.RightSection>
            <S.ResultCard>
              <S.ResultTitle>{t('expectResult')}</S.ResultTitle>

              {tab === 'PNL' && (
                <>
                  <S.ResultRow>
                    <S.ResultLabel>{t('entryPrice')}</S.ResultLabel>
                    <S.ResultValue>
                      {pnlResult?.entryPriceResult
                        ? `${pnlResult.entryPriceResult.toFormat(2)} USDT`
                        : '0.00 USDT'}
                    </S.ResultValue>
                  </S.ResultRow>
                  <S.ResultRow>
                    <S.ResultLabel>{t('pnl')}</S.ResultLabel>
                    <S.ResultValue
                      $color={
                        pnlResult?.pnl?.isGreaterThan(0)
                          ? COLORS.lightGreen
                          : pnlResult?.pnl?.isLessThan(0)
                            ? COLORS.lightRed
                            : '#ffffff'
                      }
                    >
                      {pnlResult?.pnl
                        ? `${pnlResult.pnl.toFormat(4)} USDT`
                        : '0.0000 USDT'}
                    </S.ResultValue>
                  </S.ResultRow>

                  <S.ResultRow>
                    <S.ResultLabel>{t('ROI')}</S.ResultLabel>
                    <S.ResultValue
                      $color={
                        pnlResult?.roi?.isGreaterThan(0)
                          ? COLORS.lightGreen
                          : pnlResult?.roi?.isLessThan(0)
                            ? COLORS.lightRed
                            : '#ffffff'
                      }
                    >
                      {pnlResult?.roi
                        ? `${pnlResult.roi.toFormat(2)} %`
                        : '0.00 %'}
                    </S.ResultValue>
                  </S.ResultRow>
                </>
              )}

              {tab === 'LIQUID' && (
                <S.ResultRow>
                  <S.ResultLabel>{t('liqPrice')}</S.ResultLabel>
                  <S.ResultValue>
                    {liquidResult
                      ? `${liquidResult.toFormat(2)} USDT`
                      : '0.00 USDT'}
                  </S.ResultValue>
                </S.ResultRow>
              )}
            </S.ResultCard>
          </S.RightSection>
        </S.ContentSection>
      </S.CalculatorContainer>
    </S.CustomModal>
  );
};

export default TradeCalculator;

const ArroawIcon = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
    >
      <path
        d="M7 9L10 12"
        stroke="white"
        stroke-width="1.4"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M10 12L13 9"
        stroke="white"
        stroke-width="1.4"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  );
};
